import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo } from 'react';
import { LinkContent } from './Content/LinkContent.esm.js';
import { tv } from 'tailwind-variants';
import { focusStyles } from '../../styles/focusStyles.esm.js';
import { LinkAnchor } from './LinkAnchor.esm.js';
import { LinkButton } from './LinkButton.esm.js';
import { cn } from '../../utils/cn.esm.js';

const linkStyles = tv({
    slots: {
        base: `
      iris:inline-flex
      iris:w-max
      iris:items-baseline
      iris:justify-center
      iris:gap-(--comp-space-gap-link-icon)
      iris:tracking-(--comp-type-tracking-link)
      iris:text-(--comp-color-content-link-rest)
      iris:duration-(--comp-motion-duration-link)
      iris:ease-(--comp-motion-easing-link)
      iris:hover:text-(--comp-color-content-link-hover)
      iris:focus-visible:rounded-xs
      iris:active:text-(--comp-color-content-link-pressed)
    `,
        baseFont: "iris:font-(family-name:--comp-type-font-link)",
    },
    variants: {
        variant: {
            inline: {
                base: "iris:underline",
            },
            standalone: {
                base: `
          iris:flex
          iris:min-h-(--comp-size-height-link)
          iris:no-underline
          iris:hover:underline
          iris:active:underline
        `,
            },
        },
        inverse: {
            false: {
                base: focusStyles.default,
            },
            true: {
                base: `
          iris:text-(--comp-color-content-link-inverse-rest)
          iris:hover:text-(--comp-color-content-link-inverse-hover)
          iris:active:text-(--comp-color-content-link-inverse-pressed)
          ${focusStyles.inverse}
        `,
            },
        },
        weight: {
            regular: {
                base: "iris:font-(--comp-type-weight-link-regular)!",
            },
            bold: {
                base: "iris:font-(--comp-type-weight-link-bold)!",
            },
        },
        size: {
            md: {
                base: "iris:text-(length:--comp-type-size-link-md)",
            },
            sm: {
                base: "iris:text-(length:--comp-type-size-link-sm)",
            },
            xs: {
                base: `iris:text-(length:--comp-type-size-link-xs)`,
            },
            xxs: {
                base: `iris:text-(length:--comp-type-size-link-xxs)`,
            },
        },
        iconPosition: {
            left: {
                base: `iris:flex-row`,
            },
            right: {
                base: `iris:flex-row-reverse`,
            },
        },
    },
    defaultVariants: {
        variant: "inline",
        size: "md",
        weight: "regular",
        iconPosition: "left",
    },
});

/**
 * Link component with intelligent rendering based on props.
 *
 * **Three Rendering Modes:**
 *
 * 1. **Anchor Mode** (when `href` is provided):
 *    - Renders as `<a>` element for traditional navigation
 *    - Use for external links, internal page navigation, and downloadable resources
 *    - Example: `<Link href="/about">About Us</Link>`
 *
 * 2. **Next.js Integration** (when `asChild` is true):
 *    - Renders as `<span>` wrapper for Next.js Link composition
 *    - Use with Next.js Link component for client-side routing
 *    - Example: `<NextLink href="/dashboard"><Link asChild>Dashboard</Link></NextLink>`
 *
 * 3. **Button Mode** (when neither `href` nor `asChild` are provided):
 *    - Renders as `<button>` element for JavaScript-based interactions
 *    - Use for SPA navigation, modal triggers, form actions, or analytics tracking
 *    - Maintains visual consistency with link styles while providing button semantics
 *    - Example: `<Link onClick={() => navigate('/dashboard')}>Dashboard</Link>`
 *
 * **Button Mode Use Cases:**
 * - React Router navigation: `onClick={() => navigate('/path')}`
 * - Analytics tracking: `onClick={() => trackEvent('link_clicked')}`
 * - Form validation: `onClick={() => validateAndSubmit()}`
 * - Modal/dialog triggers: `onClick={() => setIsOpen(true)}`
 *
 * @param href - URL for anchor mode navigation
 * @param asChild - Enable Next.js Link composition mode
 * @param onClick - Click handler (typically used in button mode)
 * @param variant - Visual style: 'inline' or 'standalone'
 * @param size - Text size: 'xxs' | 'xs' | 'sm' | 'md'
 */
const Link = forwardRef((_a, ref) => {
    var { className, children, icon, inverse = false, size = "md", weight = "regular", iconPosition = "left", iconSize = "md", variant = "inline", asChild, href } = _a, props = __rest(_a, ["className", "children", "icon", "inverse", "size", "weight", "iconPosition", "iconSize", "variant", "asChild", "href"]);
    const classes = useMemo(() => linkStyles({
        variant,
        size,
        iconPosition,
        weight,
        inverse,
    }), [variant, size, iconPosition, weight, inverse]);
    const content = (jsx(LinkContent, { icon: icon, size: size, iconSize: iconSize, children: children }));
    // NextLink case
    if (asChild) {
        return (jsx("span", Object.assign({ ref: ref, className: cn(classes.base({ class: className }), classes.baseFont()), tabIndex: 0 }, props, { children: content })));
    }
    if (href) {
        return (jsx(LinkAnchor, Object.assign({ ref: ref, className: cn(classes.base({ class: className }), classes.baseFont()), tabIndex: 0, href: href }, props, { children: content })));
    }
    return (jsx(LinkButton, Object.assign({ ref: ref, className: cn(classes.base({ class: className }), classes.baseFont()), tabIndex: 0 }, props, { children: content })));
});
// Add display name for better debugging
Link.displayName = "Link";

export { Link };
